import { useNavigate } from 'react-router'
import { useIsMobile } from '../hooks/useMediaQuery'
import { colors, spacing, fontSize, radius, shadow } from '../styles/theme'

const FEATURES = [
  {
    icon: '📄',
    title: 'Word templates or drag-and-drop',
    body: 'Design in Microsoft Word with {{curlyBraces}} variables — or skip Word: start from a ready-made layout and drag in blocks like letterheads, line-item tables and signature lines, wired to your data. Live preview as you build.',
  },
  {
    icon: '⚡',
    title: 'One API call, any format',
    body: 'POST a template ID and a JSON payload and the response is the document: PDF, DOCX, XLSX, PPTX, ODF or HTML. SDKs for Node, Python, .NET, Java, Go and PHP.',
  },
  {
    icon: '🔁',
    title: 'Loops, conditionals & formatters',
    body: 'Repeat table rows with {{#each}}, show or hide sections with {{#if}}, and format dates, numbers and currency in the reader\'s locale. One layout, many languages: translate the static text per locale, with paid AI drafts on Ultimate to start from. Images, barcodes, QR codes and charts come straight from your data.',
  },
  {
    icon: '🚦',
    title: 'Test, then promote to Live',
    body: 'Iterate freely in Test. When a version is ready, promote it to Live — now or at a scheduled time. Live API keys only ever render the promoted version; demote it in one click.',
  },
  {
    icon: '🕓',
    title: 'Every version kept',
    body: 'Each change is a new version you can compare side by side. Roll back by promoting an earlier one, or copy any version into a brand-new template.',
  },
  {
    icon: '📦',
    title: 'Batch, async & delivery',
    body: 'Render thousands of documents from a CSV or spreadsheet, queue renders asynchronously, get signed webhooks when they finish, and deliver to Google Drive, Dropbox, OneDrive or SharePoint.',
  },
  {
    icon: '🗄️',
    title: 'Connect your data',
    body: 'Pull payloads on a schedule from Postgres, MySQL, SQL Server, MongoDB, BigQuery, Snowflake or Redshift, or let another system trigger renders through an inbound connector.',
  },
  {
    icon: '🤖',
    title: 'AI-native authoring',
    body: 'Generate a template from a prompt, map fields semantically, and connect Claude, Cursor or any MCP-enabled assistant to list templates and render documents in conversation.',
  },
  {
    icon: '🖼️',
    title: 'Embed in your app',
    body: 'Show live document previews in your own UI. Exchange your API key for a short-lived token and render in an iframe without exposing credentials.',
  },
]

const STEPS = [
  { step: '1', title: 'Upload a template', body: 'Drop in a .docx with {{variable}} placeholders. DocMorphia extracts every variable and the payload shape automatically.' },
  { step: '2', title: 'Iterate in Test', body: 'Edit, upload new versions and render them with a Test key. Test output is watermarked, so a draft is never mistaken for the real thing.' },
  { step: '3', title: 'Promote to Live', body: 'Promote the version you are happy with. Your Live key renders it by template ID, and keeps rendering it while you work on the next one.' },
]

export default function LandingPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, #0A1413 0%, #0D4A46 50%, #0F9D92 100%)`,
        color: '#fff',
        padding: isMobile ? `${spacing.xxl} ${spacing.lg}` : `80px ${spacing.lg}`,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(15,157,146,0.25)',
            border: '1px solid rgba(95,217,207,0.5)',
            borderRadius: radius.lg,
            padding: `4px ${spacing.md}`,
            fontSize: fontSize.sm,
            color: '#5FD9CF',
            marginBottom: spacing.lg,
            fontWeight: 500,
          }}>
            Word templates → documents, via API
          </div>
          <h1 style={{
            fontSize: isMobile ? '2rem' : '3rem',
            fontWeight: 900,
            lineHeight: 1.1,
            margin: `0 0 ${spacing.lg}`,
            letterSpacing: '-1px',
          }}>
            Document generation that actually fits your workflow
          </h1>
          <p style={{
            fontSize: isMobile ? fontSize.md : fontSize.lg,
            color: '#94a3b8',
            margin: `0 0 ${spacing.xl}`,
            lineHeight: 1.7,
          }}>
            Upload a Word template, supply a JSON payload, receive a pixel-perfect document.
            Iterate safely in Test, promote to Live when it is right.
          </p>
          <div style={{ display: 'flex', gap: spacing.md, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/register')}
              style={{
                padding: `14px ${spacing.xl}`,
                background: colors.primary,
                color: '#fff',
                border: 'none',
                borderRadius: radius.md,
                fontSize: fontSize.md,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 0 0 4px rgba(15,157,146,0.3)',
              }}
            >
              Start free trial
            </button>
            <button
              onClick={() => navigate('/demo')}
              style={{
                padding: `14px ${spacing.xl}`,
                background: 'transparent',
                color: '#fff',
                border: '1px solid #475569',
                borderRadius: radius.md,
                fontSize: fontSize.md,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try the demo →
            </button>
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <div style={{
        borderBottom: `1px solid ${colors.border}`,
        background: colors.surface,
        padding: `${spacing.md} ${spacing.lg}`,
        textAlign: 'center',
      }}>
        <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
          Generate invoices, contracts, offer letters, certificates, reports — anything you template in Word
        </span>
      </div>

      {/* How it works */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: `${spacing.xxl} ${spacing.lg}` }}>
        <h2 style={{ textAlign: 'center', fontSize: isMobile ? fontSize.xl : '2rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.xxl}` }}>
          From template to production in three steps
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: spacing.xl,
        }}>
          {STEPS.map(({ step, title, body }) => (
            <div key={step} style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.lg,
              padding: spacing.xl,
              boxShadow: shadow.sm,
            }}>
              <div style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: colors.primaryLight,
                color: colors.primary,
                fontWeight: 800,
                fontSize: fontSize.md,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: spacing.md,
              }}>{step}</div>
              <div style={{ fontWeight: 700, fontSize: fontSize.md, color: colors.text, marginBottom: spacing.sm }}>{title}</div>
              <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.6 }}>{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Demo CTA */}
      <section style={{
        background: colors.primaryLight,
        border: `1px solid ${colors.border}`,
        borderLeft: 'none',
        borderRight: 'none',
        padding: `${spacing.xxl} ${spacing.lg}`,
        textAlign: 'center',
      }}>
        <h2 style={{ fontSize: isMobile ? fontSize.xl : '1.75rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.sm}` }}>
          See it in action — right now
        </h2>
        <p style={{ fontSize: fontSize.md, color: colors.textSecondary, margin: `0 0 ${spacing.lg}`, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
          Pick a sample template, fill in values, and watch the PDF update live.
          No account required.
        </p>
        <button
          onClick={() => navigate('/demo')}
          style={{
            padding: `12px ${spacing.xl}`,
            background: colors.primary,
            color: '#fff',
            border: 'none',
            borderRadius: radius.md,
            fontSize: fontSize.md,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: shadow.md,
          }}
        >
          Open live demo →
        </button>
      </section>

      {/* Features grid */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: `${spacing.xxl} ${spacing.lg}` }}>
        <h2 style={{ textAlign: 'center', fontSize: isMobile ? fontSize.xl : '2rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.xxl}` }}>
          Everything you need, nothing you don't
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: spacing.lg,
        }}>
          {FEATURES.map(({ icon, title, body }) => (
            <div key={title} style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.lg,
              padding: spacing.xl,
              boxShadow: shadow.sm,
            }}>
              <div style={{ fontSize: '1.75rem', marginBottom: spacing.md }}>{icon}</div>
              <div style={{ fontWeight: 700, fontSize: fontSize.md, color: colors.text, marginBottom: spacing.sm }}>{title}</div>
              <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.6 }}>{body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Switching + trust */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: `0 ${spacing.lg} ${spacing.xxl}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: spacing.lg }}>
          {[
            { title: 'Switching from Carbone, Docmosis or Handlebars?', body: 'Import your existing templates and DocMorphia converts their tags for you — on every plan.', cta: 'Template guide →', to: '/docs/templates' },
            { title: 'Built for security reviews', body: 'MFA on every plan, role-based access, scoped API keys, encryption at rest, retention controls, legal hold and a tamper-evident audit trail.', cta: 'Security →', to: '/security' },
          ].map(({ title, body, cta, to }) => (
            <div key={title} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: spacing.xl, background: colors.primaryLight }}>
              <div style={{ fontWeight: 700, fontSize: fontSize.md, color: colors.text, marginBottom: spacing.sm }}>{title}</div>
              <div style={{ fontSize: fontSize.base, color: colors.textSecondary, lineHeight: 1.6, marginBottom: spacing.md }}>{body}</div>
              <button onClick={() => navigate(to)} style={{ background: 'none', border: 'none', padding: 0, color: colors.primary, fontWeight: 700, fontSize: fontSize.base, cursor: 'pointer' }}>{cta}</button>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section style={{
        background: colors.surface,
        borderTop: `1px solid ${colors.border}`,
        borderBottom: `1px solid ${colors.border}`,
        padding: `${spacing.xxl} ${spacing.lg}`,
        textAlign: 'center',
      }}>
        <h2 style={{ fontSize: isMobile ? fontSize.xl : '1.75rem', fontWeight: 800, color: colors.text, margin: `0 0 ${spacing.sm}` }}>
          Simple, transparent pricing
        </h2>
        <p style={{ fontSize: fontSize.md, color: colors.textSecondary, margin: `0 0 ${spacing.lg}` }}>
          Three plans with published prices, billed per page, rendering up to 8, 16 or 32 documents at once. Every plan starts with a 30-day free trial.
        </p>
        <button
          onClick={() => navigate('/pricing')}
          style={{
            padding: `12px ${spacing.xl}`,
            background: 'transparent',
            color: colors.primary,
            border: `2px solid ${colors.primary}`,
            borderRadius: radius.md,
            fontSize: fontSize.md,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          View pricing →
        </button>
      </section>

      {/* Bottom CTA */}
      <section style={{
        background: `linear-gradient(135deg, #0A1413 0%, #0D4A46 100%)`,
        color: '#fff',
        padding: isMobile ? `${spacing.xxl} ${spacing.lg}` : `80px ${spacing.lg}`,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? fontSize.xl : '2rem', fontWeight: 800, margin: `0 0 ${spacing.md}`, letterSpacing: '-0.5px' }}>
            Ready to ship document generation?
          </h2>
          <p style={{ fontSize: fontSize.md, color: '#94a3b8', margin: `0 0 ${spacing.xl}`, lineHeight: 1.7 }}>
            Start a 30-day trial, upload your first template, and have a working render in under 10 minutes.
          </p>
          <button
            onClick={() => navigate('/register')}
            style={{
              padding: `14px ${spacing.xxl}`,
              background: colors.primary,
              color: '#fff',
              border: 'none',
              borderRadius: radius.md,
              fontSize: fontSize.md,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 0 0 4px rgba(15,157,146,0.3)',
            }}
          >
            Start free trial
          </button>
        </div>
      </section>
    </div>
  )
}
